import { FindingRepository } from "./repository";
import { ScanRepository } from "../scans/repository";
import { labelColor, displayDate } from "../utils";

interface FindingData {
    id: string;
    uuid: string;
    name: string;
    status: string;
    projectName: string;
    packageName: string;
    location: string;
    currentVersion: string;
    remediation: string;
    highestSecureVersion: string;
    secureVersions: string[];
}

export const findingDetailView = (initialData: FindingData) => ({
    repo: new ScanRepository(),
    findingRepo: new FindingRepository(),
    ciRelativePath(path: string) {
        if (!path) return '';
        const ciPrefixes = [
            '/github/workspace/',
            '/builds/',
            '/var/lib/jenkins/workspace/',
            '/home/circleci/project/',
            '/opt/atlassian/pipelines/agent/build/'
        ];

        for (const prefix of ciPrefixes) {
            if (path.startsWith(prefix)) {
                return path.slice(prefix.length);
            }
        }
        const parts = path.split('/');
        return parts.length > 2 ? parts.slice(-2).join('/') : path;
    },
    modalData: null as any,
    isModalOpen: false,
    modalId: null as string | null,
    findingStatus: initialData.status,
    labelColor,
    displayDate,

    openFixModal() {
        this.modalData = {
            id: initialData.id,
            name: initialData.name,
            project: {
                name: initialData.projectName,
            },
            details: {
                package_id: initialData.packageName,
                location: initialData.location,
                current_version: initialData.currentVersion,
            },
            remediation: initialData.remediation,
            highest_secure_version: initialData.highestSecureVersion,
            secureVersions: initialData.secureVersions,
        };
        this.modalId = 'fix-modal';
        this.isModalOpen = true;
    },

    openIgnoreModal() {
        this.modalData = {
            id: initialData.id,
            uuid: initialData.uuid,
            name: initialData.name,
            project: {
                name: initialData.projectName,
            },
            details: {
                package_id: initialData.packageName,
            }
        };
        this.modalId = 'ignore-modal';
        this.isModalOpen = true;
    },

    async submitIgnore(detail: { uuid: string; reason?: string; expires?: string }) {
        if (!detail.uuid) {
            console.error('No UUID provided for ignore request');
            // @ts-ignore
            this.$dispatch('notify', { content: 'Error: No finding UUID found', type: 'error' });
            return;
        }

        const payload: { reason: string; expires?: string } = {
            reason: detail.reason || ''
        };

        if (detail.expires) {
            payload.expires = detail.expires;
        }

        try {
            const submitEvent = new CustomEvent('set-ignore-status', {
                detail: { status: 'submitting' }
            });
            document.dispatchEvent(submitEvent);

            const response = await fetch('/api/v1/findings/' + detail.uuid + '/ignore/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': document.querySelector<HTMLInputElement>('[name=csrfmiddlewaretoken]')?.value || ''
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                this.findingStatus = 'closed';
                this.closeModal();
                // @ts-ignore
                this.$dispatch('notify', {
                    content: 'Finding ignored successfully!',
                    type: 'success'
                });
                // @ts-ignore
                this.$dispatch('clear-ignore-form');
                // @ts-ignore
                this.$dispatch('finding-ignored');
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'HTTP ' + response.status + ': ' + response.statusText);
            }
        } catch (error: any) {
            console.error('Error ignoring finding:', error);
            // @ts-ignore
            this.$dispatch('notify', {
                content: 'Error ignoring finding: ' + error.message,
                type: 'error'
            });
        } finally {
            const resetEvent = new CustomEvent('set-ignore-status', {
                detail: { status: 'ready' }
            });
            document.dispatchEvent(resetEvent);
        }
    },

    closeModal() {
        this.isModalOpen = false;
        this.modalId = null;
        this.modalData = null;
    }
});
